import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { m } from 'framer-motion'
import { tw } from 'utils/classnames'

import { Button } from '@repo/studio-design-system-ui-button'

import { useSheetDrag } from './sheet-drag'
import type { SheetDirection } from './sheet-drag'

const SheetRoot = DialogPrimitive.Root

const SheetTrigger = DialogPrimitive.Trigger

const SheetClose = DialogPrimitive.Close

const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Overlay>>
}) => (
  <DialogPrimitive.Overlay
    className={tw(
      'fixed inset-0 bg-black/50',
      'backdrop-blur-0 transition-[backdrop-filter] data-[state=open]:backdrop-blur-sm data-[state=open]:backdrop-brightness-75',
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
)
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName

const sheetDirectionClasses: Record<string, string> = {
  top: tw(
    'rounded-b-2 inset-x-0 top-0',
    'data-[state=closed]:slide-out-to-top',
    'data-[state=open]:slide-in-from-top',
  ),
  bottom: tw(
    'rounded-t-2 inset-x-0 bottom-0',
    'data-[state=closed]:slide-out-to-bottom',
    'data-[state=open]:slide-in-from-bottom',
  ),
  left: tw(
    'rounded-r-2 inset-y-0 left-0 h-full w-3/4 sm:max-w-sm',
    'data-[state=closed]:slide-out-to-left',
    'data-[state=open]:slide-in-from-left',
  ),
  right: tw(
    'rounded-l-2 inset-y-0 right-0 h-full w-3/4 sm:max-w-sm',
    'data-[state=closed]:slide-out-to-right',
    'data-[state=open]:slide-in-from-right',
  ),
}

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  showCloseButton?: boolean
  showOverlay?: boolean
  direction: 'top' | 'bottom' | 'left' | 'right'
  close?: () => void
  collapsedHeight?: string
  onCollapse?: () => void
  onExpand?: () => void
  isCollapsed?: boolean
  handleClassName?: string
  overlayClassName?: string
  overlayStyle?: React.CSSProperties
  container?: HTMLElement | null
}

const EDGE_ORDER: Record<SheetDirection, string> = {
  top: 'order-last',
  bottom: 'order-first',
  left: 'order-last',
  right: 'order-first',
}

const CLOSE_CORNER: Record<SheetDirection, string> = {
  top: 'bottom-4 right-4',
  bottom: 'right-4 top-4',
  left: 'right-4 top-4',
  right: 'right-4 top-4',
}

function SheetEdges({
  direction,
  handleClassName,
  drag,
}: {
  direction: SheetDirection
  handleClassName?: string
  drag: ReturnType<typeof useSheetDrag>
}) {
  const vertical = direction === 'top' || direction === 'bottom'
  return (
    <>
      {vertical ? (
        <div
          role="slider"
          tabIndex={0}
          aria-orientation="horizontal"
          className={tw(
            'flex h-6 w-full cursor-grab touch-none items-center justify-center active:cursor-grabbing',
            EDGE_ORDER[direction],
            handleClassName,
          )}
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
        >
          <div className="h-1 w-12 rounded-full bg-gray-800 transition-colors hover:bg-gray-600" />
        </div>
      ) : (
        <div
          role="slider"
          tabIndex={0}
          aria-orientation="vertical"
          className={tw(
            'flex h-full w-6 cursor-grab touch-none items-center justify-center self-center active:cursor-grabbing',
            EDGE_ORDER[direction],
            handleClassName,
          )}
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
        >
          <div className="h-12 w-1 rounded-full bg-gray-800 transition-colors hover:bg-gray-600" />
        </div>
      )}
    </>
  )
}

function SheetDismiss({ direction, visible }: { direction: SheetDirection; visible: boolean }) {
  return visible ? (
    <SheetClose asChild className={tw('absolute z-10', CLOSE_CORNER[direction])}>
      <Button iconOnly size="xs" variant="ghost" aria-label="Close">
        <XCloseIcon className="size-4 text-gray-100" />
      </Button>
    </SheetClose>
  ) : null
}

function SheetBody({
  direction,
  collapsedHeight,
  isCollapsed,
  children,
}: {
  direction: SheetDirection
  collapsedHeight?: string
  isCollapsed: boolean
  children: React.ReactNode
}) {
  const horizontal = direction === 'left' || direction === 'right'
  return (
    <div
      className={tw(
        'content-container flex min-h-0 flex-1 flex-col overflow-auto overscroll-contain px-4 pb-4',
        horizontal ? 'min-h-full' : 'min-w-full',
      )}
      style={isCollapsed && collapsedHeight ? { height: collapsedHeight } : undefined}
    >
      {children}
    </div>
  )
}

const SheetContent = ({
  className,
  children,
  direction,
  showCloseButton = false,
  showOverlay = true,
  close,
  collapsedHeight,
  onCollapse,
  onExpand,
  isCollapsed = false,
  handleClassName,
  overlayClassName,
  overlayStyle,
  container,
  ...props
}: SheetContentProps) => {
  const drag = useSheetDrag({
    direction,
    collapsedHeight,
    isCollapsed,
    close,
    onCollapse,
    onExpand,
  })

  return (
    <DialogPrimitive.Portal container={container ?? undefined}>
      <DialogPrimitive.Title className="hidden" />
      {/* Radix attaches the modal scroll lock to the Overlay. Without one, a sheet opened over
          another modal (the lightbox) sits outside that modal's lock and can't scroll. */}
      {showOverlay ? (
        <SheetOverlay className={overlayClassName} style={overlayStyle} />
      ) : (
        <DialogPrimitive.Overlay className="hidden" />
      )}
      <DialogPrimitive.Content
        className={tw(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out shadow-lg',
          sheetDirectionClasses[direction ?? 'right'],
          'bg-gray-1000 flex flex-col',
          'isolate',
          className,
          // Last so `tw` can't drop this in favour of a caller's position utility.
          // A sheet that is not fixed re-enters document flow, grows the page past
          // the viewport and scrolls away from the overlay pinned over it.
          'fixed',
        )}
        asChild
        {...props}
      >
        <m.div style={{ transform: drag.transform }} transition={{ duration: 0 }}>
          <SheetEdges direction={direction} handleClassName={handleClassName} drag={drag} />
          <SheetDismiss direction={direction} visible={showCloseButton} />
          <SheetBody
            direction={direction}
            collapsedHeight={collapsedHeight}
            isCollapsed={isCollapsed}
          >
            {children}
          </SheetBody>
        </m.div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}
SheetContent.displayName = DialogPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={tw('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={tw('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Title>>
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={tw('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
)
SheetTitle.displayName = DialogPrimitive.Title.displayName

const SheetDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Description>>
}) => (
  <DialogPrimitive.Description
    ref={ref}
    className={tw('text-muted-foreground text-sm', className)}
    {...props}
  />
)
SheetDescription.displayName = DialogPrimitive.Description.displayName

export {
  SheetRoot,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
